import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { Box } from '@mui/material';
import styled from '@emotion/styled';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { mediaApi } from '../../utils/api';
import { FormContainer } from '../common/form/FormContainer';
import { FormGroup } from '../common/form/FormGroup';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import { getCompanySettings, selectCompanySettings, selectCompanySettingsState } from '../../store/entities/companySettings';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { getCompanyCustomization } from '../../store/entities/companyCustomization';
import { BannerInput } from '../qcsCompanySettings/BannerInput';
import { EmailLogoPreview, EmailLogoPreviewToggle } from '../qcsCompanySettings/EmailLogoPreview';
import { CompanySettingsDto } from '@api/models/settings/CompanySettingsDto';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { companySettingsService } from '@api/services/services';

//The height and both colours hold a handful of characters, so they share one narrow width instead
//of a grid fraction. 14rem is the smallest width that keeps the longest translated label (de) inside
//the outlined notch without ellipsis.
const FIELD_WIDTH = '14rem';

const NarrowInput = styled(Input)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: FIELD_WIDTH,
    flexShrink: 0,
  },
}));

//A native colour input paints its whole content box, so only its height is capped, to the height of
//a normal input row. Stretched over the full field it reads as a colour slab instead of a control.
const ColorSwatchInput = styled(NarrowInput)(({ theme }) => ({
  '& input[type="color"]': {
    boxSizing: 'content-box',
    //0.75 + 2 + 0.75 = 3.5rem, the height of every other field.
    height: '2rem',
    padding: '0.75rem 0.875rem',
    cursor: 'pointer',
  },
  '& input[type="color"]::-webkit-color-swatch-wrapper': {
    padding: 0,
  },
  //Without an outline a white or very pale colour is invisible against the white field.
  '& input[type="color"]::-webkit-color-swatch': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '3px',
  },
  '& input[type="color"]::-moz-color-swatch': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '3px',
  },
}));

const ColorRow = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
});

//Below this the groups stack; above it they sit side by side. A fixed breakpoint rather than
//flex-wrap, so the switch happens at one predictable width instead of wherever the content spills.
const SIDE_BY_SIDE_FROM = '@media (min-width: 950px)';

//Stacked, the preview belongs directly under the logo it previews, with the colours after it.
//Side by side, it spans the full width below both groups — the mock-up is wider than either column.
const SectionsGrid = styled.div({
  display: 'grid',
  gridTemplateAreas: '"logo" "preview" "colors"',
  gridTemplateColumns: 'minmax(0, 1fr)',
  [SIDE_BY_SIDE_FROM]: {
    gridTemplateAreas: '"logo colors" "preview preview"',
    //The fr column absorbs the preview's width, so opening it cannot widen the logo column.
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    alignItems: 'start',
    columnGap: '2rem',
  },
});

const LogoArea = styled.div({
  gridArea: 'logo',
  minWidth: 0,
});

const PreviewArea = styled.div({
  gridArea: 'preview',
  minWidth: 0,
});

const ColorsArea = styled.div({
  gridArea: 'colors',
  minWidth: 0,
});

export const CompSettTabAppearance: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companySettings = useAppSelector(selectCompanySettings);
  const [oldFilesToRemove, setOldFilesToRemove] = useState<string[]>([]);
  //Held here so the toggle can sit in the action row while the preview renders below it.
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  useEffect(() => {
    dispatch(getCompanySettings());
  }, [dispatch, identityCompanyId]);

  const handleSubmit = async (data: CompanySettingsDto) => {
    await companySettingsService.updateSettings(data);

    for (const oldFile of oldFilesToRemove) {
      try {
        await mediaApi.deleteFile(oldFile);
      } catch {
        //Ignore error.
      }
    }

    dispatch(getCompanySettings());
    dispatch(getCompanyCustomization());
  };

  const handleRemoveOldFile = (document: string) => {
    setOldFilesToRemove([...oldFilesToRemove, document]);
  };

  return (
    <Box mt={2}>
      <ShowResourceContent resource={resources.getSettings} fetchStateSelector={selectCompanySettingsState}>
        <FormikDebounce<CompanySettingsDto>
          initialValues={companySettings}
          enableReinitialize
          validationSchema={Yup.object().shape({
            emailLogoHeight: validations.intPositiveOptional(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <SectionsGrid>
                <LogoArea>
                  <FormGroup name="qcsCompanySettings.banner">
                    <BannerInput
                      onRemoveOldFile={handleRemoveOldFile}
                      fieldSlot={
                        <NarrowInput
                          name="emailLogoHeight"
                          label={t('admin.company.emailLogoHeight')}
                          type="number"
                          min={1}
                          disablePasswordManager
                          clearable
                        />
                      }
                      actionSlot={<EmailLogoPreviewToggle show={showEmailPreview} onToggle={() => setShowEmailPreview(!showEmailPreview)} />}
                    />
                  </FormGroup>
                </LogoArea>

                <PreviewArea>
                  <EmailLogoPreview show={showEmailPreview} />
                </PreviewArea>

                <ColorsArea>
                  <FormGroup name="qcsCompanySettings.colors">
                    <ColorRow>
                      <ColorSwatchInput name="secondaryColor" type="color" label={t('qcsCompanySettings.primaryColor')} shrinkLabel />
                      <ColorSwatchInput name="primaryColor" type="color" label={t('qcsCompanySettings.secondaryColor')} shrinkLabel />
                    </ColorRow>
                  </FormGroup>
                </ColorsArea>
              </SectionsGrid>

              <ShowError resource={resources.updateSettings} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </Box>
  );
};
